import { useEffect, useMemo, useRef } from 'react';
import { publish, EVENTS } from '@sendible/event-manager';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { PAGE_SIZE_DEFAULT, useGetHolidays, useInfiniteGetMessagesByDay } from '@sendible/frontendv2/src/models/calendar';
import { DroppableDay, DraggableCard, Holidays } from '..';
import { CalendarCard } from '../../../CalendarCard';
import { CreationButton } from '../CreationButton';
import { fullDateToMonthString, getCalendarCardMedias } from '../../../../utils';
import { useCalendarContext } from '../../../../context';

interface DayColumnProps {
  iso: CalendarMonthString;
  isPast: boolean;
  isToday: boolean;
  summary?: CalendarData;
  pageSize?: number;
}

export const WeekDayColumn = ({ iso, isPast, isToday, summary, pageSize = PAGE_SIZE_DEFAULT }: DayColumnProps) => {
  const user = useBridgeUser();
  const { density, openSidebar, userWeekStartDay, showHolidays } = useCalendarContext();
  const hasExtraData = (summary?.messageCount ?? 0) > pageSize;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteGetMessagesByDay(iso, hasExtraData);
  const { data: holidays, isFetching, isLoading } = useGetHolidays(iso, userWeekStartDay, { range: 'day', enabled: showHolidays });

  const isGettingHolidays = showHolidays && isFetching && isLoading;
  const showHoliday = !isPast && showHolidays && holidays && holidays.length > 0;

  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  const extra = useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((p) => (Array.isArray(p) ? p : (p as CalendarData)?.messages || []));
  }, [data]);

  const merged = useMemo(() => {
    const base = summary?.messages || [];

    if (!extra.length) return base;

    const seen = new Set<CalendarMessage['id']>();
    const combined: CalendarMessage[] = [];

    base.forEach((m) => {
      if (!seen.has(m.id)) {
        seen.add(m.id);
        combined.push(m);
      }
    });
    extra.forEach((m) => {
      if (!seen.has(m.id)) {
        seen.add(m.id);
        combined.push(m);
      }
    });
    combined.sort((a, b) => (a.sendDate < b.sendDate ? -1 : a.sendDate > b.sendDate ? 1 : 0));

    return combined;
  }, [summary, extra]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasExtraData) return;

    const el = sentinelRef.current;

    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }
      },
      { root: el.parentElement, rootMargin: '160px 0px 160px 0px', threshold: 0.01 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [hasExtraData, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <DroppableDay
      dayKey={iso}
      isToday={isToday}
      view="weekly"
    >
      {showHoliday && (
        <Holidays
          isLoading={isGettingHolidays}
          holidays={holidays}
          onClick={(text: string, image: string) =>
            publish(EVENTS.OPEN_COMPOSE, { mode: 'new', date: iso, offset: user.timezone, preFillText: text, preFillImage: image })
          }
        />
      )}

      {merged.map((msg) => (
        <DraggableCard
          key={msg.id}
          message={msg}
          dayKey={iso}
        >
          <CalendarCard
            id={msg.id}
            onClick={() => openSidebar('message', fullDateToMonthString(msg.sendDateLocalized), msg.id)}
            deliveryStatus={msg.deliveryStatus}
            postAt={msg.sendDateLocalized}
            destinations={msg.destinations || []}
            status={msg.status}
            medias={getCalendarCardMedias(msg.body)}
            recurrence={msg.recurrence}
            title={msg.body?.messageText || msg.subject}
            type={density === 'compact' ? 'compact' : 'weekly'}
            workspace={isWorkspaceMode ? msg.author || msg.workspace : msg.workspace}
          />
        </DraggableCard>
      ))}
      {!isPast && (
        <CreationButton
          onClick={() => publish(EVENTS.OPEN_COMPOSE, { mode: 'new', date: iso, offset: user.timezone })}
          view="weekly"
        />
      )}

      {hasExtraData && <div ref={sentinelRef} />}
    </DroppableDay>
  );
};
